/**
 * Issue Tracker API Service
 * Handles all API calls for the Issue Tracker functionality (Phase 3.5a)
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface Issue {
  id: string;
  report_id: string;
  detected_by: string;
  issue_type: string;
  title: string;
  description: string;
  what_would_fix: string;
  status: 'pending_review' | 'approved' | 'rejected' | 'sent_to_client' | 'resolved' | 'wont_fix';
  priority: 'critical' | 'high' | 'medium' | 'low';
  created_at: string;
  updated_at?: string;
  affected_amount?: number;
  resolution?: string;
  client_response?: string;
  status_history?: StatusChange[];
}

export interface StatusChange {
  from_status: string;
  to_status: string;
  changed_by: string;
  changed_at: string;
  reason?: string;
}

export interface IssueFilters {
  status?: string;
  priority?: string;
  issue_type?: string;
  report_id?: string;
}

export interface IssueStats {
  total: number;
  pending_review: number;
  approved: number;
  sent_to_client: number;
  resolved: number;
  by_priority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

class IssueTrackerApi {
  private async getHeaders(): Promise<HeadersInit> {
    // For now, return empty headers - auth will be added later
    return {
      'Content-Type': 'application/json'
    };
  }

  async getIssues(filters?: IssueFilters): Promise<Issue[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();
    
    if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters?.priority && filters.priority !== 'all') params.append('priority', filters.priority);
    if (filters?.issue_type && filters.issue_type !== 'all') params.append('issue_type', filters.issue_type);
    if (filters?.report_id) params.append('report_id', filters.report_id);
    
    const queryString = params.toString();
    const url = `${BACKEND_URL}/api/v1/issues/list${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch issues: ${response.statusText}`);
    }
    return response.json();
  }

  async getIssue(issueId: string): Promise<Issue> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/issues/detail/${issueId}`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch issue: ${response.statusText}`);
    }
    return response.json();
  }

  async getIssueStats(): Promise<IssueStats> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/issues/stats`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch issue stats: ${response.statusText}`);
    }
    return response.json();
  }

  // Backend: POST /issues/{id}/approve requires ?user_id= (query) + body {request_text}
  // (ApproveIssueRequest.request_text is required). Previously sent {approved_by} with no
  // user_id → 422. Returns a transition result {success, issue_id, new_status, message},
  // NOT a full Issue — callers should refetch the issue.
  async approveIssue(
    issueId: string,
    userId: string,
    requestText: string
  ): Promise<{ success?: boolean; issue_id?: string; new_status?: string; message?: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/issues/${issueId}/approve?user_id=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_text: requestText })
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to approve issue: ${response.statusText}`);
    }
    return response.json();
  }

  // No backend /reject route exists. "Reject" maps to the DISMISSED transition:
  // POST /issues/{id}/dismiss?user_id= + body {reason} (DismissIssueRequest.reason required).
  async rejectIssue(
    issueId: string,
    userId: string,
    reason: string
  ): Promise<{ success?: boolean; issue_id?: string; new_status?: string; message?: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/issues/${issueId}/dismiss?user_id=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason })
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to reject issue: ${response.statusText}`);
    }
    return response.json();
  }

  // Resolve an issue with a matched document → POST /issues/{id}/resolve?user_id= +
  // {document_id}. This drives the RESOLVED transition (a positive outcome recorded as
  // resolved_by_document), NOT /dismiss — so a document-resolved issue is not mislabeled
  // as dismissed. An issue not yet in a resolvable state returns 400 (surfaced honestly).
  async resolveIssue(
    issueId: string,
    userId: string,
    documentId: string
  ): Promise<{ success?: boolean; issue_id?: string; new_status?: string; message?: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/issues/${issueId}/resolve?user_id=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ document_id: documentId, confidence: 'high' })
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to resolve issue: ${response.statusText}`);
    }
    return response.json();
  }

  async confirmDocumentMatch(matchId: string, userId: string, confirmed: boolean): Promise<{
    confirmed: boolean;
    issue_resolved?: boolean;
    issue_id?: string;
  }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/issues/matches/${matchId}/confirm?user_id=${userId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ confirmed })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Failed to confirm match: ${response.statusText}`);
    }
    return response.json();
  }

  async getIssuesByReport(reportId: string): Promise<Issue[]> {
    return this.getIssues({ report_id: reportId });
  }

  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await fetch(`${BACKEND_URL}/api/v1/issues/health`);
    if (!response.ok) {
      throw new Error('Issue Tracker service unavailable');
    }
    return response.json();
  }
}

export const issueTrackerApi = new IssueTrackerApi();
export default issueTrackerApi;
